import { redirect } from '@tanstack/react-router';

import { getUserPermissions, hasPermission, APP_PERMISSIONS } from './permissions';
import type { AppPermission } from './permissions';

const ROUTE_PERMISSIONS: Record<string, AppPermission | AppPermission[]> = {
	'/entities': APP_PERMISSIONS.MANAGE_ENTITIES,
	'/users': APP_PERMISSIONS.MANAGE_USERS,
	'/patients': [APP_PERMISSIONS.CARE_ADMIN, APP_PERMISSIONS.PATIENT_CARE],
	'/referrals': APP_PERMISSIONS.ENROLLMENT_ADMIN,
};

/**
 * Creates a route guard that restricts access based on user permissions.
 *
 * This guard ensures that the user's permissions are loaded and then checks
 * if the user has the required permission to access the requested path.
 * Users with the `*` permission bypass all checks.
 * If access is denied, it redirects the user to the dashboard.
 *
 * @param path - The route path to check access for (e.g., '/entities')
 * @returns A function suitable for use in TanStack Router's `beforeLoad` hook
 *
 * @example
 * // In a route file:
 * export const Route = createFileRoute('/entities/')({
 *   beforeLoad: ensureRouteAccess('/entities'),
 * });
 */
export const ensureRouteAccess = (path: string) => async () => {
	const { permissions } = await getUserPermissions();

	const required = ROUTE_PERMISSIONS[path];

	if (!required) return;

	const requiredPermissions = Array.isArray(required) ? required : [required];
	const hasAccess = requiredPermissions.some((p) => hasPermission(permissions, p));

	if (!hasAccess) {
		throw redirect({
			to: '/dashboard',
			search: { view: 'queue' },
		});
	}
};
